import { message } from 'antd';
import { useIntl } from 'react-intl';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useApiClient } from 'utils/apiClient';
import { downloadFile } from 'ShorePay/utils';
import {
  GetTransactionsQueryParams,
  GetTransactionsResponse,
  ReportPayload,
  TransactionDetails,
} from 'utils/apiClient/services/shorepay/types';
import { handleErrorNotification } from 'Common/utils';

export const queryKeys = {
  all: () => ['shorepay', 'transactions'],
  list: (filters: {}) => [...queryKeys.all(), filters],
  timelineList: (filters: {}) => [...queryKeys.all(), 'timeline', filters],
  details: (id: string) => [...queryKeys.all(), id],
};

// eslint-disable-next-line import/prefer-default-export
export const useTransactions = (
  filters: GetTransactionsQueryParams = {},
  opts: Omit<
    UseQueryOptions<
      GetTransactionsResponse,
      unknown,
      GetTransactionsResponse,
      ReturnType<typeof queryKeys.list>
    >,
    'queryKey' | 'queryFn'
  > = {}
) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: queryKeys.list(filters),
    queryFn: () => apiClient.ShorePay.fetchTransactions(filters),
    staleTime: 1000,
    ...opts,
  });
};

export const useTransactionDetails = (
  id: string,
  opts: Omit<
    UseQueryOptions<
      TransactionDetails,
      unknown,
      TransactionDetails,
      ReturnType<typeof queryKeys.details>
    >,
    'queryKey' | 'queryFn'
  > = {}
) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: queryKeys.details(id),
    queryFn: () => apiClient.ShorePay.fetchTransactionDetails(id),
    staleTime: 1000,
    ...opts,
  });
};

export const useTimelineTransactions = (
  filters: GetTransactionsQueryParams = {}
) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: queryKeys.timelineList(filters),
    queryFn: () => apiClient.ShorePay.fetchTimelineTransactions(filters),
    staleTime: 1000,
  });
};

export const useCreateRefund = (tId: string, opts: UseMutationOptions = {}) => {
  const intl = useIntl();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.ShorePay.createTransactionRefund(tId),
    onSuccess: () => {
      message.success(
        intl.formatMessage({ defaultMessage: 'Refund created successfully' })
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.details(tId) });
      queryClient.refetchQueries({ type: 'active' });
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
    ...opts,
  });
};

export const useGenerateTransactionReport = () => {
  const apiClient = useApiClient();
  const { locale } = useIntl();
  return useMutation({
    mutationFn: (payload: ReportPayload) =>
      apiClient.ShorePay.createTransactionReport({
        ...payload,
        language: locale.toLowerCase(),
      }),
  });
};

export const useDownloadTransactionReport = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useQuery({
    queryKey: ['shorepay', `transaction-report`],
    queryFn: async () => {
      try {
        const data = await apiClient.ShorePay.fetchTransactionReport();
        downloadFile(data?.download_url, 'download.csv');
      } catch (error) {
        handleErrorNotification({
          error,
          lang: intl.locale.toLowerCase(),
        });
      }
    },
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmailTransactionReport = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.ShorePay.mailTransactionReport,
    onSuccess: () => {
      message.success(
        intl.formatMessage({ defaultMessage: 'Mail sent successfully' })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
